import React, { useState } from 'react';
import { Send, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    nombreCondominio: '',
    direccion: '',
    telefono: '',
    email: '',
    tipoUsuario: '',
    cantidadUnidades: '',
    consulta: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, tipoUsuario: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setIsSuccess(true);
        toast.success('¡Formulario enviado exitosamente! Nos contactaremos pronto.');
        // Reset form
        setFormData({
          nombre: '',
          nombreCondominio: '',
          direccion: '',
          telefono: '',
          email: '',
          tipoUsuario: '',
          cantidadUnidades: '',
          consulta: ''
        });
        setTimeout(() => setIsSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error al enviar formulario:', error);
      toast.error('Error al enviar el formulario. Por favor intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#082E5C] mb-4">
            Solicita tu Diagnóstico Gratuito
          </h2>
          <p className="text-xl text-gray-600">
            Completa el formulario y nos contactaremos contigo para analizar las necesidades de tu propiedad
          </p>
        </div>

        {/* Form Card */}
        <Card className="border-2 border-[#00D4D8]/20 shadow-2xl">
          <CardHeader className="bg-gradient-to-br from-[#082E5C] to-[#0a3a6e] text-white rounded-t-lg">
            <CardTitle className="text-2xl">Formulario de Contacto</CardTitle>
            <CardDescription className="text-gray-200">
              Ingresa tus datos y nos pondremos en contacto a la brevedad
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Nombre */}
                <div className="space-y-2">
                  <Label htmlFor="nombre" className="text-[#082E5C] font-medium">
                    Nombre Completo *
                  </Label>
                  <Input
                    id="nombre"
                    name="nombre"
                    type="text"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    placeholder="Juan Pérez"
                    className="border-2 focus:border-[#00D4D8] transition-colors"
                  />
                </div>

                {/* Tipo de Usuario */}
                <div className="space-y-2">
                  <Label htmlFor="tipoUsuario" className="text-[#082E5C] font-medium">
                    ¿Quién eres? *
                  </Label>
                  <Select onValueChange={handleSelectChange} value={formData.tipoUsuario} required>
                    <SelectTrigger className="border-2 focus:border-[#00D4D8]">
                      <SelectValue placeholder="Selecciona una opción" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residente">Residente</SelectItem>
                      <SelectItem value="comite">Miembro del Comité</SelectItem>
                      <SelectItem value="mayordomo">Mayordomo</SelectItem>
                      <SelectItem value="proveedor">Proveedor</SelectItem>
                      <SelectItem value="administrador">Administrador de Propiedades</SelectItem>
                      <SelectItem value="otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Nombre Condominio */}
                <div className="space-y-2">
                  <Label htmlFor="nombreCondominio" className="text-[#082E5C] font-medium">
                    Nombre del Condominio *
                  </Label>
                  <Input
                    id="nombreCondominio"
                    name="nombreCondominio"
                    type="text"
                    value={formData.nombreCondominio}
                    onChange={handleChange}
                    required
                    placeholder="Edificio Las Condes"
                    className="border-2 focus:border-[#00D4D8] transition-colors"
                  />
                </div>

                {/* Dirección */}
                <div className="space-y-2">
                  <Label htmlFor="direccion" className="text-[#082E5C] font-medium">
                    Dirección *
                  </Label>
                  <Input
                    id="direccion"
                    name="direccion"
                    type="text"
                    value={formData.direccion}
                    onChange={handleChange}
                    required
                    placeholder="Av. Providencia 1234, Santiago"
                    className="border-2 focus:border-[#00D4D8] transition-colors"
                  />
                </div>

                {/* Cantidad de Unidades */}
                <div className="space-y-2">
                  <Label htmlFor="cantidadUnidades" className="text-[#082E5C] font-medium">
                    Cantidad de Unidades
                  </Label>
                  <Input
                    id="cantidadUnidades"
                    name="cantidadUnidades"
                    type="number"
                    value={formData.cantidadUnidades}
                    onChange={handleChange}
                    placeholder="Ej: 50"
                    className="border-2 focus:border-[#00D4D8] transition-colors"
                    min="1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Teléfono */}
                <div className="space-y-2">
                  <Label htmlFor="telefono" className="text-[#082E5C] font-medium">
                    Teléfono de Contacto *
                  </Label>
                  <Input
                    id="telefono"
                    name="telefono"
                    type="tel"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    placeholder="+56 9 1234 5678"
                    className="border-2 focus:border-[#00D4D8] transition-colors"
                  />
                </div>

                {/* Email */}
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#082E5C] font-medium">
                    Email de Contacto *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="tu@email.com"
                    className="border-2 focus:border-[#00D4D8] transition-colors"
                  />
                </div>
              </div>

              {/* Consulta */}
              <div className="space-y-2">
                <Label htmlFor="consulta" className="text-[#082E5C] font-medium">
                  Consulta o Mensaje *
                </Label>
                <Textarea
                  id="consulta"
                  name="consulta"
                  value={formData.consulta}
                  onChange={handleChange}
                  required
                  placeholder="Cuéntanos qué necesitas... ¿Qué desafíos enfrenta tu condominio actualmente?"
                  rows={5}
                  className="border-2 focus:border-[#00D4D8] transition-colors resize-none"
                />
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                {isSuccess ? (
                  <div className="flex items-center justify-center space-x-2 text-green-600 bg-green-50 p-4 rounded-lg">
                    <CheckCircle2 size={24} />
                    <span className="font-medium">¡Formulario enviado exitosamente!</span>
                  </div>
                ) : (
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#00D4D8] hover:bg-[#00B8BC] text-white text-lg py-6 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl group"
                  >
                    {isSubmitting ? (
                      'Enviando...'
                    ) : (
                      <>
                        Enviar Solicitud
                        <Send className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-600">
            O escríbenos directamente a:{' '}
            <a href="mailto:eduardo.briceno@tucondominiodigital.com" className="text-[#00D4D8] hover:text-[#00B8BC] font-medium transition-colors">
              eduardo.briceno@tucondominiodigital.com
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
